/**
 * Centralized error handling utility for the agent system.
 * Provides specialized handling for different types of errors including API key issues,
 * tool execution failures, and general error formatting.
 */
export class ErrorHandler {
    /**
     * Determines if an error is related to API key authentication issues.
     * Checks for common API key error patterns in the error message.
     */
    private static isApiKeyError(error: Error): boolean {
        const errorMessage = error.message.toLowerCase();
        return (
            errorMessage.includes('401') ||
            errorMessage.includes('unauthorized') ||
            errorMessage.includes('invalid api key') ||
            errorMessage.includes('authentication') ||
            errorMessage.includes('api key')
        );
    }

    /**
     * Handles API-related errors. With session auth in production and env vars in tests,
     * API key errors are no longer recoverable through user intervention.
     *
     * @param error The error to handle
     * @returns User-friendly error message
     */
    static handleApiError(error: Error): string {
        if (this.isApiKeyError(error)) {
            // In test environments, API key errors indicate configuration issues
            let hasEnvApiKey = false;
            try {
                hasEnvApiKey = !!process.env.OPENAI_API_KEY;
            } catch {
                // process not defined (browser without DefinePlugin)
            }
            if (hasEnvApiKey) {
                return 'API key authentication failed. Please check your test environment configuration.';
            }
            // In production, auth errors likely indicate session/proxy issues
            return 'Authentication failed. Please refresh the page and try again.';
        }
        return error.message;
    }

    /**
     * Checks if an error is related to tool execution or validation.
     */
    static isToolExecutionError(error: Error): boolean {
        return error.message.includes('tool execution') || error.message.includes('validation');
    }

    /**
     * Formats tool execution errors with consistent messaging.
     *
     * @param toolName Name of the tool that encountered the error
     * @param error The error that occurred
     * @returns Formatted error message
     */
    static formatToolError(toolName: string, error: Error): string {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        return `Error executing ${toolName}: ${errorMessage}`;
    }
}
